import CounterpartyContactsForm from "components/cargoOwners/contactsForm";
import { openUniversalDocumentCreateForm } from "components/finances/documentCreation";
import DocumentListFilters, {
    DocumentListCounter,
    withDisabledTableControls,
} from "components/finances/documentListFilters";
import {
    getDocumentUpdateSubscriptionKey,
    subscribeToDocumentUpdates,
} from "components/finances/documentUpdateSubscription";
import BookIndex from "components/shared/book/list";
import { Transition } from "components/shared/column";
import DropdownBtn from "components/shared/dropdown";
import InlineBtn from "components/shared/inlineBtn";
import SharedReactTable from "components/shared/reactTable";
import { dateTimeToView } from "helpers/date";
import {
    bidStatuses,
    distributionBidStatuses,
    documentBusinessTypes,
    documentBusinessTypesEnum,
    relationToTypesEnum,
    universalDocumentSendStatuses,
    universalDocumentSendStatusesEnum,
    universalDocumentStatuses,
    universalDocumentSystems,
} from "helpers/enums";
import { paths } from "helpers/paths";
import { DateRangeColumnFilter, rowIndexColumn, SelectColumnFilter } from "helpers/reactTable";
import { tableContainer } from "helpers/redux";
import { downloadURI, enumLabel, formatNumber } from "helpers/system";
import Tooltip from "rc-tooltip";
import React from "react";
import { Link } from "react-router-dom";
import { namesList as counterpartiesList } from "sources/books/cargoOwners";
import { delItem, getDocumentFile, setSendStatus } from "sources/books/finance";
import DownloadFilesByPeriod from "./downloadFilesByPeriod";

const relatedObjectStatusEnums = {
    [relationToTypesEnum.Bid]: bidStatuses,
    [relationToTypesEnum.DistributionBid]: distributionBidStatuses,
};

class FinancesDocuments extends BookIndex {
    documentUpdateSubscriptions = {};

    isUnmounted = false;

    get tableColumns() {
        return [
            rowIndexColumn,
            {
                Header: "Название",
                accessor: "name",
                filterable: true,
                Cell: ({ value, row: { original } }) =>
                    original.documentExists ? (
                        original.isGenerating ? (
                            value || "Документ формируется"
                        ) : (
                            <Link to={paths.financesDocumentsShow(original.id)}>{value || original.id}</Link>
                        )
                    ) : (
                        <>
                            <span className="text-muted">Документ не создан</span>
                            <div>{this.renderCreateDocumentButton(original)}</div>
                        </>
                    ),
            },
            {
                Header: "Связанный объект",
                accessor: "relatedToId",
                filterable: true,
                filterType: "int",
                Cell: ({ value, row: { original } }) => {
                    if (!value) {
                        return null;
                    }

                    switch (original.relatedToType) {
                        case relationToTypesEnum.Bid:
                            return <Link to={paths.bidShow("bid", value)}>Заявка: {value}</Link>;
                        case relationToTypesEnum.DistributionBid:
                            return <Link to={paths.distributionBidShow(value)}>Заказ: {value}</Link>;
                        default:
                            return value;
                    }
                },
            },
            {
                Header: "Дата документа",
                accessor: "date",
                filterable: true,
                filterType: "dateTimeRange",
                sortable: true,
                Filter: DateRangeColumnFilter,
                Cell: ({ value }) => dateTimeToView(value),
                CSVExport: ({ value }) => dateTimeToView(value),
            },
            {
                Header: "Статус документа",
                accessor: "status",
                filterable: true,
                filterType: "enum",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            inputProps: { name: "status" },
                            options: universalDocumentStatuses,
                        }}
                    />
                ),
                Cell: ({ value, row: { original } }) =>
                    !original.documentExists ? null : original.isGenerating ? (
                        <Tooltip placement="bottom" overlay="Формирование документа может занимать до 5 секунд">
                            <div className="d-flex align-items-center">
                                <span className="icon-uEA79-spinner animation-rspin pr-1" />В обработке
                            </div>
                        </Tooltip>
                    ) : (
                        enumLabel(universalDocumentStatuses, value)
                    ),
                CSVExport: ({ value }) => enumLabel(universalDocumentStatuses, value),
            },
            {
                Header: "Статус отправки",
                accessor: "sendStatus",
                filterable: true,
                filterType: "enum",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            inputProps: { name: "sendStatus" },
                            options: universalDocumentSendStatuses,
                        }}
                    />
                ),
                Cell: ({ value, row: { original } }) => {
                    if (!original.documentExists) {
                        return null;
                    }

                    return (
                        !original.isGenerating && (
                            <>
                                <div className="pb-1">{enumLabel(universalDocumentSendStatuses, value)}</div>
                                <div
                                    className="btn btn-xs btn-outline-primary"
                                    onClick={() =>
                                        this.changeStatusHandle(
                                            original.id,
                                            universalDocumentSendStatusesEnum.SentToEmail,
                                            original.counterparty?.id,
                                        )
                                    }
                                >
                                    Отправить на почту
                                </div>
                                {original.sendAt && (
                                    <div className="small text-muted">{dateTimeToView(original.sendAt)}</div>
                                )}
                            </>
                        )
                    );
                },
                CSVExport: ({ value }) => enumLabel(universalDocumentSendStatuses, value),
            },
            {
                Header: "Система документооборота",
                accessor: "system",
                filterable: true,
                filterType: "enum",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            inputProps: { name: "system" },
                            options: universalDocumentSystems,
                        }}
                    />
                ),
                Cell: ({ value, row: { original } }) =>
                    original.documentExists ? enumLabel(universalDocumentSystems, value) : null,
                CSVExport: ({ value }) => enumLabel(universalDocumentSystems, value),
            },
            {
                Header: "НДС",
                accessor: "vat",
                width: 100,
                filterable: true,
                filterType: "int",
                Cell: ({ value, row: { original } }) =>
                    original.documentExists && !original.isGenerating ? (value ? `${value}%` : "Без НДС") : null,
            },
            {
                Header: "Стоимость без НДС",
                accessor: "totalWithVatExcluded",
                width: 100,
                filterable: true,
                filterType: "int",
                Cell: ({ value, row: { original } }) =>
                    original.documentExists &&
                    !original.isGenerating &&
                    `${formatNumber(value)} ${original.currency?.displayName || ""}`,
                CSVExport: ({ value, original }) => `${formatNumber(value)} ${original.currency?.displayName || ""}`,
            },
            {
                Header: "Статус заявки",
                accessor: "bidStatus",
                Cell: ({ value, row: { original } }) =>
                    enumLabel(relatedObjectStatusEnums[original.relatedToType] || bidStatuses, value),
            },
            {
                Header: "Дата выполнения перевозчиком",
                accessor: "hiredCarCompletedAt",
                Cell: ({ value }) => dateTimeToView(value),
                CSVExport: ({ value }) => dateTimeToView(value),
            },
            {
                Header: "Контрагент",
                accessor: "counterparty",
                id: "counterparty/id",
                sortable: true,
                filterable: true,
                filterType: "select",
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            url: counterpartiesList,
                            inputProps: { name: "counterpartid" },
                            propName: "name",
                        }}
                    />
                ),
                Cell: ({ value }) => value && <Link to={paths.cargoOwnersShow(value.id)}>{value.name}</Link>,
            },
            {
                Header: "Файлы",
                accessor: "files",
                filterable: false,
                Cell: ({ row: { original } }) => {
                    if (!original.documentExists) {
                        return null;
                    }
                    const downloadFile = (extension) =>
                        getDocumentFile(original.id, extension).then(
                            (res) => res && downloadURI(res.accessUrlLink, res.originalFileName),
                        );
                    const items =
                        this.props.type === documentBusinessTypesEnum.Contract
                            ? [
                                  { text: "DOCX", action: () => downloadFile("docx") },
                                  { text: "Загруженный XML", action: () => downloadFile("xml") },
                              ]
                            : [
                                  { text: "PDF", action: () => downloadFile("pdf") },
                                  { text: "УПД XML", action: () => downloadFile("utd.xml") },
                              ];
                    const removeItem = () => {
                        this.setState({ loading: true });
                        this.props.modal({
                            name: "confirm",
                            title: "Вы действительно хотите удалить документ?",
                            onSuccessAction: () => this.props.mergeParams({}),
                            onCloseAction: () => this.setState({ loading: false }),
                            buttons: { confirm: "Да", cancel: "Нет" },
                            action: () => delItem({ id: original.id }),
                        });
                    };
                    return (
                        <>
                            <DropdownBtn
                                cname="btn btn-xs btn-outline-primary"
                                text="Скачать"
                                items={items}
                                contentCname="font-size-14"
                            />
                            {!original.deletedAt && (
                                <div>
                                    <div className="btn btn-xs btn-outline-danger" onClick={removeItem}>
                                        Удалить
                                    </div>
                                </div>
                            )}
                        </>
                    );
                },
                CSVExport: ({ value }) => value?.map((item) => item.originalFileName).join("; "),
            },
            {
                Header: "Дата удаления",
                accessor: "deletedAt",
                filterable: true,
                filterType: "dateTimeRange",
                sortable: true,
                Filter: DateRangeColumnFilter,
                Cell: ({ value }) => dateTimeToView(value),
                CSVExport: ({ value }) => dateTimeToView(value),
            },
        ];
    }

    renderCreateDocumentButton = (item) =>
        this.props.canEditBooks ? (
            <div
                className="btn btn-xs btn-outline-primary"
                onClick={() =>
                    openUniversalDocumentCreateForm({
                        item,
                        documentType: this.props.type,
                        modal: this.props.modal,
                        roles: this.props.roles,
                        refresh: () => this.refreshAfterDocumentCreate(item),
                        setLoading: (loading) => this.setState({ loading }),
                    })
                }
            >
                Создать документ
            </div>
        ) : null;

    renderCounter = () => <DocumentListCounter count={this.props.count} />;

    componentWillUnmount() {
        this.isUnmounted = true;
        this.clearDocumentUpdateSubscriptions();
    }

    refreshList = () => {
        if (this.isUnmounted) {
            return;
        }

        this.props.getList({ ...this.props.params, type: this.props.type });
    };

    removeDocumentUpdateSubscription = (key) => {
        delete this.documentUpdateSubscriptions[key];
    };

    clearDocumentUpdateSubscription = (key) => {
        const subscription = this.documentUpdateSubscriptions[key];

        if (!subscription) {
            return;
        }

        delete this.documentUpdateSubscriptions[key];
        subscription.close();
    };

    clearDocumentUpdateSubscriptions = () => {
        Object.keys(this.documentUpdateSubscriptions).forEach(this.clearDocumentUpdateSubscription);
    };

    subscribeToDocumentUpdates = (item) => {
        if (this.isUnmounted) {
            return;
        }

        const key = getDocumentUpdateSubscriptionKey(item);

        this.clearDocumentUpdateSubscription(key);

        const subscription = subscribeToDocumentUpdates({
            item,
            onUpdate: this.refreshList,
            onClose: this.removeDocumentUpdateSubscription,
        });

        if (subscription) {
            this.documentUpdateSubscriptions[subscription.key] = subscription;
        }
    };

    refreshAfterDocumentCreate = (item) => {
        if (this.isUnmounted) {
            return;
        }

        this.subscribeToDocumentUpdates(item);
        this.refreshList();
    };

    changeStatusHandle = (id, status, counterpartyId) => {
        return setSendStatus({ id, status }).then((res) => {
            if (res) {
                this.getList(this.props);
            } else {
                this.props.modal({
                    name: "default",
                    windowMod: "sm",
                    title: "Заполните контактные данные",
                    CustomBody: CounterpartyContactsForm,
                    bodyData: { counterpartyId },
                });
            }
        });
    };

    renderListComponent = () => (
        <SharedReactTable
            {...this.props}
            withoutPagination
            isLoadingList={this.props.isLoadingList || this.state?.loading}
            columns={withDisabledTableControls(this.state.tableColumns)}
            getList={(params) => this.props.getList({ ...params, type: this.props.type })}
        />
    );

    close = () => this.props.history.push(paths.finances);

    renderHead = () => (
        <div className="book-list__head-wrap">
            <div className="book-list__head">
                <div className="book-list__head-close close close--sm" onClick={this.close} />
                <div className="book-list__head-title">
                    {enumLabel(documentBusinessTypes, this.props.type)}
                    {this.renderCounter()}
                </div>
            </div>
        </div>
    );

    downloadFiles = () => {
        this.props.modal({
            name: "default",
            title: "Скачать все файлы",
            windowMod: "sm",
            CustomBody: DownloadFilesByPeriod,
            bodyData: { type: this.props.type },
        });
    };

    renderActions() {
        const { mergeParams } = this.props;
        const actions = [
            { value: "Обновить", cname: "refresh", active: true, action: mergeParams, params: {} },
            { value: "Скачать все файлы", cname: "download", active: true, action: this.downloadFiles },
        ];
        return actions.map((item, index) => <InlineBtn key={index} {...item} />);
    }

    render() {
        return (
            <div className="page-content">
                <Transition dir="full">
                    <div className="page-book__list book-list list">
                        {this.renderHead()}
                        <DocumentListFilters params={this.props.params} mergeParams={this.props.mergeParams} />
                        <div className="container-fluid my-2">{this.renderActions()}</div>
                        {this.renderListComponent()}
                    </div>
                </Transition>
            </div>
        );
    }
}

const InvoiceDocuments = (props) => <FinancesDocuments type={documentBusinessTypesEnum.Invoice} {...props} />;
const ContractDocuments = (props) => <FinancesDocuments type={documentBusinessTypesEnum.Contract} {...props} />;
const AcceptanceCertificateDocuments = (props) => (
    <FinancesDocuments type={documentBusinessTypesEnum.AcceptanceCertificate} {...props} />
);
const TaxInvoiceDocuments = (props) => <FinancesDocuments type={documentBusinessTypesEnum.TaxInvoice} {...props} />;

export default {
    [documentBusinessTypesEnum.Contract]: tableContainer(ContractDocuments, "finances-contract"),
    [documentBusinessTypesEnum.Invoice]: tableContainer(InvoiceDocuments, "finances-invoice"),
    [documentBusinessTypesEnum.AcceptanceCertificate]: tableContainer(
        AcceptanceCertificateDocuments,
        "finances-acceptance-certificate",
    ),
    [documentBusinessTypesEnum.TaxInvoice]: tableContainer(
        TaxInvoiceDocuments,
        "finances-acceptance-certificate-invoice",
    ),
};
